import React, { Component } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { deleteIncident, getIncidents, getUnresolvedIncidentsCount, updateIncident } from '../../../api/incidents';
import { buildMonthRange, truncateText } from '../../../helpers';
import { formatDate } from '../../../helpers/dates';
import { SET_UNRESOLVED_INCIDENTS_COUNT } from '../../../redux/actions/types';
import Select from '../../elements/Select';
import IncidentModal from '../calendar/IncidentModal';
import type { HoursBreakdown, Incident, OvertimePeriodName, Project, User } from '../../../types';

const STATUS_OPTIONS = [
  { id: '', title: 'All statuses' },
  { id: 'unresolved', title: 'Unresolved' },
  { id: 'resolved', title: 'Resolved' },
];

type OwnProps = {
  projectId: number;
  selectedMonth: number;
  selectedYear: number;
  projects: Array<Project>;
  currentUser: User;
};

type Props = OwnProps & {
  users: Array<User>;
  onUpdateUnresolvedCount: (count: number) => void;
};

type BreakdownTableProps = {
  periods: HoursBreakdown;
  totalRow?: number;
};

const BreakdownTable: React.FC<BreakdownTableProps> = ({ periods, totalRow }) => {
  const entries = Object.entries(periods) as Array<[OvertimePeriodName, { hours: number; multiplier: number; effective_hours: number }]>;

  return (
    <table className="table table-sm table-bordered mb-0">
      <thead className="thead-light">
        <tr>
          <th>Period</th>
          <th className="text-right">Actual Hours</th>
          <th className="text-right">Multiplier</th>
          <th className="text-right">Effective Hours</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(([period, entry]) => (
          <tr key={period}>
            <td>{PERIOD_LABELS[period] ?? period}</td>
            <td className="text-right">{Number(entry.hours).toFixed(2)}</td>
            <td className="text-right">{Number(entry.multiplier).toFixed(2)}x</td>
            <td className="text-right">{Number(entry.effective_hours).toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
      {totalRow !== undefined && (
        <tfoot>
          <tr className="font-weight-bold">
            <td colSpan={3} className="text-right">Total effective hours</td>
            <td className="text-right">{Number(totalRow).toFixed(2)}</td>
          </tr>
        </tfoot>
      )}
    </table>
  );
};

const PERIOD_LABELS: Record<OvertimePeriodName, string> = {
  weekday_before_hours: 'Weekday before-hours (06:00–08:00)',
  weekday_regular: 'Weekday regular (08:00–17:00)',
  weekday_after_hours: 'Weekday after-hours (17:00–22:00)',
  weekday_night_shift: 'Weekday night-shift (22:00–06:00)',
  weekend_holiday_day_shift: 'Weekend/Holiday day-shift (08:00–20:00)',
  weekend_holiday_night_shift: 'Weekend/Holiday night-shift (20:00–08:00)',
};

function formatDateWithTimeBelow(iso: string) {
  const d = new Date(iso);

  return (
    <>
      <div>{formatDate(d, 'dd.MM.yyyy')}</div>
      <div className="text-muted small">
        {d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
      </div>
    </>
  );
}

function formatIncidentDateWithRange(startIso: string, endIso: string) {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const startTime = start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const endTime = end.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  return (
    <>
      <div>{formatDate(start, 'dd.MM.yyyy')}</div>
      <div className="text-muted small">{`${startTime} - ${endTime}`}</div>
    </>
  );
}

type State = {
  incidents: Array<Incident>;
  loading: boolean;
  error: string | null;
  statusFilter: string;
  monthFilter: string;
  editIncident: Incident | null;
  expandedIncidentId: number | null;
  resolvingIncidentId: number | null;
  deletingIncidentId: number | null;
};

class IncidentsTable extends Component<Props, State> {
  state: State = {
    incidents: [],
    loading: false,
    error: null,
    statusFilter: '',
    monthFilter: `${this.props.selectedYear}-${String(this.props.selectedMonth).padStart(2, '0')}`,
    editIncident: null,
    expandedIncidentId: null,
    resolvingIncidentId: null,
    deletingIncidentId: null,
  };

  componentDidMount() {
    this.fetchIncidents();
  }

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.projectId !== this.props.projectId ||
      prevProps.selectedMonth !== this.props.selectedMonth ||
      prevProps.selectedYear !== this.props.selectedYear
    ) {
      const { selectedYear, selectedMonth } = this.props;
      const monthFilter = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
      this.setState({ monthFilter }, this.fetchIncidents);
    }
  }

  fetchIncidents = async () => {
    const { projectId } = this.props;
    const { statusFilter, monthFilter } = this.state;

    this.setState({ loading: true, error: null });

    try {
      const incidents = await getIncidents(projectId, {
        status: statusFilter || undefined,
        month: monthFilter,
      });
      this.setState({ incidents, loading: false });
    } catch {
      this.setState({
        error: 'Failed to load incidents',
        loading: false,
      });
    }
  };

  handleStatusFilterChange = (event: any) => {
    this.setState({ statusFilter: event.target.value }, this.fetchIncidents);
  };

  handleMonthFilterChange = (event: any) => {
    this.setState({ monthFilter: event.target.value }, this.fetchIncidents);
  };

  handleEditClick = (incident: Incident) => {
    this.setState({ editIncident: incident });
  };

  handleEditModalClose = () => {
    this.setState({ editIncident: null });
  };

  handleEditSubmit = () => {
    this.setState({ editIncident: null });
    this.fetchIncidents();
    this.refreshUnresolvedCount();
  };

  refreshUnresolvedCount = async () => {
    const count = await getUnresolvedIncidentsCount();
    this.props.onUpdateUnresolvedCount(count);
  };

  handleToggleBreakdown = (incidentId: number) => {
    this.setState(prev => ({
      expandedIncidentId: prev.expandedIncidentId === incidentId ? null : incidentId,
    }));
  };

  handleDeleteClick = async (incident: Incident) => {
    if (!window.confirm(`Delete incident "${incident.title}"?`)) return;

    this.setState({ deletingIncidentId: incident.id, error: null });

    try {
      await deleteIncident(incident.project_id, incident.id);
      this.setState({ deletingIncidentId: null });
      await this.fetchIncidents();
      await this.refreshUnresolvedCount();
    } catch {
      this.setState({
        error: 'Failed to delete incident',
        deletingIncidentId: null,
      });
    }
  };

  handleMarkResolved = async (incident: Incident) => {
    this.setState({ resolvingIncidentId: incident.id, error: null });

    try {
      await updateIncident(incident.project_id, incident.id, { status: 'resolved' });
      this.setState({ resolvingIncidentId: null });
      await this.fetchIncidents();
      await this.refreshUnresolvedCount();
    } catch {
      this.setState({
        error: 'Failed to mark incident as resolved',
        resolvingIncidentId: null,
      });
    }
  };

  monthRange = buildMonthRange(false);

  render() {
    const {
      incidents, loading, error, statusFilter, monthFilter, editIncident, expandedIncidentId,
      resolvingIncidentId, deletingIncidentId,
    } = this.state;
    const { projects, currentUser, users } = this.props;

    const monthOptions = this.monthRange;

    return (
      <div className="col-12">
        <div className="incidents-table-wrapper">
          <h3>Incidents</h3>

          <div className="incidents-filters mb-3">
            <div className="row">
              <div className="col-md-6">
                <Select
                  name="status_filter"
                  label="Status"
                  id="status_filter"
                  selectClass="form-control"
                  value={statusFilter}
                  items={STATUS_OPTIONS}
                  onSelectChange={this.handleStatusFilterChange}
                  includeChooseOption={false}
                />
              </div>
              <div className="col-md-6">
                <Select
                  name="month_filter"
                  label="Month"
                  id="month_filter"
                  selectClass="form-control"
                  value={monthFilter}
                  items={monthOptions}
                  onSelectChange={this.handleMonthFilterChange}
                  includeChooseOption={false}
                />
              </div>
            </div>
          </div>

          {loading && <p className="text-center">Loading incidents...</p>}

          {error && <p className="text-danger text-center">{error}</p>}

          {!loading && incidents.length === 0 && (
            <p className="text-center text-muted">No incidents found.</p>
          )}

          {!loading && incidents.length > 0 && (
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Reported By</th>
                  <th>Reported At</th>
                  <th>Reported To</th>
                  <th>Status</th>
                  <th>Approved By</th>
                  <th>Approved At</th>
                  <th>Reported Via</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {incidents.map(incident => {
                  const isExpanded = expandedIncidentId === incident.id;
                  const { by_user = [], combined = {} } = incident.hours_breakdown ?? {};
                  const hasBreakdown = by_user.length > 0;
                  const combinedEntries = Object.entries(combined) as Array<[OvertimePeriodName, { hours: number; multiplier: number; effective_hours: number }]>;
                  const totalEffectiveHours = combinedEntries.reduce((sum, [, e]) => sum + Number(e.effective_hours), 0);

                  return (
                    <React.Fragment key={incident.id}>
                      <tr>
                        <td>{incident.title}</td>
                        <td>{incident.reported_by || '-'}</td>
                        <td>{formatDateWithTimeBelow(incident.reported_at)}</td>
                        <td>{incident.reported_to || '-'}</td>
                        <td>
                          <span
                            className={`badge ${
                              incident.status === 'resolved'
                                ? 'badge-success'
                                : 'badge-warning'
                            }`}
                          >
                            {_.startCase(incident.status)}
                          </span>
                        </td>
                        <td>{incident.approved_by || '-'}</td>
                        <td>
                          {incident.approved_at ? formatDateWithTimeBelow(incident.approved_at) : '-'}
                        </td>
                        <td>{_.startCase(incident.reported_via) || '-'}</td>
                        <td>{truncateText(incident.description)}</td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => this.handleEditClick(incident)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              disabled={deletingIncidentId === incident.id}
                              onClick={() => this.handleDeleteClick(incident)}
                              title="Delete incident"
                            >
                              🗑
                            </button>
                            {incident.status === 'unresolved' && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                disabled={resolvingIncidentId === incident.id}
                                onClick={() => this.handleMarkResolved(incident)}
                              >
                                {resolvingIncidentId === incident.id ? 'Saving…' : 'Resolve'}
                              </button>
                            )}
                            {hasBreakdown && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-info"
                                onClick={() => this.handleToggleBreakdown(incident.id)}
                              >
                                {isExpanded ? 'Hide hours' : 'Show hours'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="incident-breakdown-row">
                          <td colSpan={10} className="p-3 bg-light">
                            <strong>Hours Breakdown</strong>

                            {by_user.map(({ user_id, user_name, periods, logs = [] }) => (
                              <div key={user_id} className="mt-3">
                                <h6 className="mb-1">{user_name}</h6>
                                {logs.length > 0 && (
                                  <table className="table table-sm table-bordered mb-2">
                                    <thead className="thead-light">
                                      <tr>
                                        <th>Date (start-end)</th>
                                        <th>Description</th>
                                        <th>Service category</th>
                                        <th>Task ID</th>
                                        <th className="text-right">Hours</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {logs.map((log, idx) => (
                                        <tr key={idx}>
                                          <td>{formatIncidentDateWithRange(log.start_time, log.end_time)}</td>
                                          <td>
                                            <div>{incident.title}</div>
                                            <div className="text-muted small">{log.description}</div>
                                          </td>
                                          <td>{log.category ? _.startCase(log.category) : '-'}</td>
                                          <td>{log.task_id || '-'}</td>
                                          <td className="text-right">{Number(log.time_spent).toFixed(2)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                                <BreakdownTable periods={periods} />
                                {(() => {
                                  const periodEntries = Object.values(periods) as Array<{ hours: number; effective_hours: number }>;
                                  const userTotalActual = periodEntries.reduce((sum, e) => sum + Number(e.hours), 0);
                                  const userTotalEffective = periodEntries.reduce((sum, e) => sum + Number(e.effective_hours), 0);

                                  return (
                                    <div className="text-right mt-1 mb-2">
                                      <span style={{ fontWeight: 'bold', marginRight: '2rem' }}>
                                        Total actual: {userTotalActual.toFixed(2)}h
                                      </span>
                                      <span style={{ fontWeight: 'bold' }}>
                                        Total effective: {userTotalEffective.toFixed(2)}h
                                      </span>
                                    </div>
                                  );
                                })()}
                              </div>
                            ))}

                            <div className="mt-3">
                              <h6 className="mb-1 font-weight-bold">Combined Total</h6>
                              <BreakdownTable periods={combined} />
                              <div className="text-right mt-1">
                                <span style={{ fontWeight: 'bold', marginRight: '2rem' }}>
                                  Total actual: {combinedEntries.reduce((sum, [, e]) => sum + Number(e.hours), 0).toFixed(2)}h
                                </span>
                                <span style={{ fontWeight: 'bold' }}>
                                  Total effective: {totalEffectiveHours.toFixed(2)}h
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {editIncident && (
          <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <IncidentModal
                  edit
                  incident={editIncident}
                  projects={projects}
                  users={users}
                  currentUser={currentUser}
                  onCloseModal={this.handleEditModalClose}
                  onSubmit={this.handleEditSubmit}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  users: state.users,
});

const mapDispatchToProps = (dispatch: any) => ({
  onUpdateUnresolvedCount: (count: number) => {
    dispatch({ type: SET_UNRESOLVED_INCIDENTS_COUNT, payload: count });
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(IncidentsTable);
